import {
	CalendarOutlined,
	DeleteOutlined,
	DollarOutlined,
	EditOutlined,
	InfoCircleOutlined,
	LeftOutlined,
	PlusOutlined,
	ShoppingCartOutlined,
} from "@ant-design/icons";

import { getClientEnv } from "@shared/env";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Avatar,
	Button,
	Checkbox,
	Form,
	Image,
	Input,
	InputNumber,
	Modal,
	message,
	Select,
	Upload,
} from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { getSaleById } from "@/modules/sales/services/sales.backend";
import type { AppRole } from "@/shared/config/navigation";
import {
	canEditRoomPrice,
	canManageObjectStructure,
	canSellRoom,
} from "@/shared/config/permissions";
import { parseApiError } from "@/shared/utils/error";
import { formatMoney } from "@/shared/utils/money";
import { blocksApi } from "../api/blocks.api";
import { buildingsApi } from "../api/buildings.api";
import { filesApi } from "../api/files.api";
import { roomsApi } from "../api/rooms.api";
import { sectionsApi } from "../api/sections.api";
import type { Room } from "../types/objects.types";
import { calculatePriceRemont, toBackendPricePerArea } from "../utils/room-price";
import { getUploadedAssetUrl } from "../utils/uploaded-asset-url";

const env = getClientEnv();

function formatUzsAmount(amount: number) {
	return `${formatMoney(amount)} UZS`;
}

const moneyInputProps = {
	formatter: (value: number | string | undefined) =>
		`${value ?? ""}`.replace(/\B(?=(\d{3})+(?!\d))/g, " "),
	parser: (value: string | undefined) => Number((value ?? "").replace(/\s/g, "")),
};

function RoomImagePreview({
	label,
	src,
	onDelete,
}: {
	label: string;
	src?: string;
	onDelete?: () => void;
}) {
	const imageUrl = getUploadedAssetUrl(env.VITE_API_URL, src);

	return (
		<div
			className="crm-imgph grow"
			style={{
				minWidth: "100px",
				height: "90px",
				overflow: "hidden",
				padding: imageUrl ? 0 : undefined,
				position: "relative",
			}}
		>
			{imageUrl ? (
				<div className="crm-image-preview-wrapper" style={{ width: "100%", height: "100%" }}>
					<Image
						alt={label}
						src={imageUrl}
						style={{ width: "100%", height: "90px", objectFit: "cover" }}
						wrapperStyle={{ width: "100%", height: "100%" }}
					/>
					{onDelete && (
						<Button
							type="text"
							icon={<DeleteOutlined style={{ color: "var(--crm-red)" }} />}
							onClick={(e) => {
								e.stopPropagation();
								onDelete();
							}}
							className="crm-image-delete-btn"
						/>
					)}
				</div>
			) : (
				<span style={{ fontSize: "10px" }}>{label}</span>
			)}
		</div>
	);
}

type RoomUploadFieldProps = {
	existingAsset?: string;
	file?: File;
	label: string;
	onFileChange: (file: File | undefined) => void;
};

function RoomUploadField({ existingAsset, file, label, onFileChange }: RoomUploadFieldProps) {
	const [previewUrl, setPreviewUrl] = useState<string>();

	useEffect(() => {
		if (file) {
			const url = URL.createObjectURL(file);
			setPreviewUrl(url);
			return () => URL.revokeObjectURL(url);
		}
		if (existingAsset) {
			setPreviewUrl(getUploadedAssetUrl(env.VITE_API_URL, existingAsset));
		} else {
			setPreviewUrl(undefined);
		}
	}, [file, existingAsset]);

	return (
		<Form.Item label={label} style={{ marginBottom: "12px" }}>
			<div className="crm-square-upload-wrapper">
				{previewUrl ? (
					<div className="crm-square-upload-preview">
						<Image
							src={previewUrl}
							alt={label}
							style={{ width: "96px", height: "96px", objectFit: "cover", borderRadius: "12px" }}
							wrapperStyle={{ width: "96px", height: "96px" }}
						/>
						<Button
							type="text"
							icon={<DeleteOutlined style={{ color: "var(--crm-red)" }} />}
							onClick={(e) => {
								e.stopPropagation();
								onFileChange(undefined);
							}}
							className="crm-square-upload-delete"
						/>
					</div>
				) : (
					<Upload
						accept="image/png,image/jpeg,image/webp"
						beforeUpload={(nextFile) => {
							onFileChange(nextFile);
							return false;
						}}
						maxCount={1}
						showUploadList={false}
					>
						<div className="crm-square-upload-btn">
							<PlusOutlined style={{ fontSize: "18px", color: "var(--crm-ink-3)" }} />
							<span
								style={{
									fontSize: "11px",
									fontWeight: 500,
									color: "var(--crm-ink-2)",
									marginTop: "4px",
								}}
							>
								{label}
							</span>
						</div>
					</Upload>
				)}
			</div>
		</Form.Item>
	);
}

export function RoomDetailPage() {
	const { roomId } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const { role } = useOutletContext<{ role: AppRole }>();
	const queryClient = useQueryClient();

	const { data: buildings = [] } = useQuery({
		queryKey: ["buildings"],
		queryFn: () => buildingsApi.getBuildings({ limit: 100 }),
	});

	const { data: blocks = [] } = useQuery({
		queryKey: ["blocks", "all"],
		queryFn: () => blocksApi.getBlocks({ limit: 100 }),
	});

	const { data: sections = [] } = useQuery({
		queryKey: ["sections", "all"],
		queryFn: () => sectionsApi.getSections({ limit: 100 }),
	});

	const { data: room, isLoading } = useQuery({
		queryKey: ["room", roomId],
		queryFn: () => {
			if (!roomId) {
				throw new Error("Room ID is required");
			}
			return roomsApi.getRoomById(roomId);
		},
		enabled: !!roomId,
	});

	const updateRoomMutation = useMutation({
		mutationFn: (data: { id: string; payload: any }) => roomsApi.updateRoom(data.id, data.payload),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["room", roomId] });
			queryClient.invalidateQueries({ queryKey: ["rooms"] });
			message.success("Xona ma'lumotlari muvaffaqiyatli yangilandi!");
			closeEditModal();
		},
		onError: (error) => {
			message.error(parseApiError(error) || "Xonani tahrirlashda xatolik yuz berdi");
		},
	});

	const deleteRoomMutation = useMutation({
		mutationFn: () => {
			if (!roomId) {
				throw new Error("Room ID is required");
			}
			return roomsApi.deleteRoom(roomId);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["rooms"] });
			message.success("Xona muvaffaqiyatli o'chirildi!");

			// Resolve parent buildingId and blockId dynamically from cached data or context state
			const currentRoom = queryClient.getQueryData<Room>(["room", roomId]);
			const section = currentRoom ? sections.find((s) => s.id === currentRoom.sectionId) : null;
			const block = section ? blocks.find((b) => b.id === section.blockId) : null;
			const building = block ? buildings.find((b) => b.id === block.buildingId) : null;

			const bId = building?.id || currentRoom?.buildingId || "";
			const blkId = block?.id || currentRoom?.blockId || "";
			const orgIdQuery = building?.orgId ? `&orgId=${building.orgId}` : "";

			navigate(`/rooms?buildingId=${bId}&blockId=${blkId}${orgIdQuery}`, {
				state: location.state,
				replace: true,
			});
		},
		onError: (error) => {
			message.error(parseApiError(error) || "Xonani o'chirishda xatolik yuz berdi");
		},
	});

	const [editModalVisible, setEditModalVisible] = useState(false);
	const [pricingModalVisible, setPricingModalVisible] = useState(false);
	const [pricingForm] = Form.useForm();
	const [calculatedSchedule, setCalculatedSchedule] = useState<any>(null);
	const [showInlineSchedule, setShowInlineSchedule] = useState(false);
	const [scheduleDownPaymentPercent, setScheduleDownPaymentPercent] = useState<number>(30);
	const [scheduleMonthsCount, setScheduleMonthsCount] = useState<number>(12);

	const savePricingMutation = useMutation({
		mutationFn: (payload: {
			installmentPrice: number;
			installmentMonths: number;
			minInitialPaymentPercent: number;
		}) => {
			if (!room?.id) {
				throw new Error("Xona topilmadi");
			}
			return roomsApi.setRoomPricing(room.id, payload);
		},
		onSuccess: () => {
			message.success("Bo'lib to'lash narxlari muvaffaqiyatli saqlandi!");
			if (room?.id) {
				queryClient.invalidateQueries({ queryKey: ["room", room.id] });
			}
			queryClient.invalidateQueries({ queryKey: ["rooms"] });
			setPricingModalVisible(false);
		},
		onError: (error) => {
			message.error(parseApiError(error) || "Narxlarni saqlashda xatolik yuz berdi");
		},
	});

	const calculateScheduleMutation = useMutation({
		mutationFn: (payload: {
			installmentPrice: number;
			monthsCount: number;
			initialPaymentPercent: number;
		}) => {
			if (!room?.id) {
				throw new Error("Xona topilmadi");
			}
			return roomsApi.calculateSchedule(room.id, payload);
		},
		onSuccess: (data) => {
			setCalculatedSchedule(data);
		},
		onError: (error) => {
			message.error(parseApiError(error) || "Grafikni hisoblashda xatolik yuz berdi");
		},
	});

	const fillPricingFormFromRoom = () => {
		const installmentPrice = room?.installmentPrice || room?.finishedPrice || room?.basePrice || 0;
		const installmentMonths = room?.installmentMonths || 12;
		const minInitialPaymentPercent = room?.minInitialPaymentPercent ?? 30;

		pricingForm.setFieldsValue({
			installmentPrice,
			installmentMonths,
			minInitialPaymentPercent,
		});
		setScheduleDownPaymentPercent(minInitialPaymentPercent);
		setScheduleMonthsCount(installmentMonths);

		return { installmentPrice, installmentMonths, minInitialPaymentPercent };
	};

	const handleOpenPricingModal = () => {
		setCalculatedSchedule(null);
		setPricingModalVisible(true);
		fillPricingFormFromRoom();
	};

	const handleCalculateSchedule = () => {
		pricingForm.validateFields().then((values) => {
			calculateScheduleMutation.mutate({
				installmentPrice: values.installmentPrice,
				monthsCount: scheduleMonthsCount,
				initialPaymentPercent: scheduleDownPaymentPercent,
			});
		});
	};

	const handleSavePricing = () => {
		pricingForm.validateFields().then((values) => {
			savePricingMutation.mutate({
				installmentPrice: values.installmentPrice,
				installmentMonths: values.installmentMonths,
				minInitialPaymentPercent: values.minInitialPaymentPercent,
			});
		});
	};

	const handlePricingFormValuesChange = (changedValues: any) => {
		if (changedValues.minInitialPaymentPercent !== undefined) {
			setScheduleDownPaymentPercent(changedValues.minInitialPaymentPercent);
		}
		if (changedValues.installmentMonths !== undefined) {
			setScheduleMonthsCount(changedValues.installmentMonths);
		}
	};
	const [planFile, setPlanFile] = useState<File>();
	const [image3dFile, setImage3dFile] = useState<File>();
	const [terraceFile, setTerraceFile] = useState<File>();
	const [form] = Form.useForm();
	const canChangeRoomPrice = canEditRoomPrice(role);
	const canCreateRoomSale = canSellRoom(role);
	const linkedSaleId = room?.client?.saleId;

	const { data: linkedSale } = useQuery({
		queryKey: ["sale", linkedSaleId, "room-inline-schedule"],
		queryFn: () => getSaleById(linkedSaleId as string),
		enabled: !!linkedSaleId && showInlineSchedule,
	});

	const showPredictiveSchedule = !room?.client && !!room?.installmentPrice;

	const { data: predictiveSchedule, isLoading: isPredictiveScheduleLoading } = useQuery({
		queryKey: [
			"room-predictive-schedule",
			room?.id,
			room?.installmentPrice,
			room?.installmentMonths,
			room?.minInitialPaymentPercent,
		],
		queryFn: () =>
			roomsApi.calculateSchedule(room?.id as string, {
				installmentPrice: room?.installmentPrice as number,
				monthsCount: room?.installmentMonths || 12,
				initialPaymentPercent: room?.minInitialPaymentPercent ?? 30,
			}),
		enabled: showPredictiveSchedule,
	});

	useEffect(() => {
		const state = location.state as { openEdit?: boolean } | null;
		if (!(state?.openEdit && room && canChangeRoomPrice)) {
			return;
		}

		form.setFieldsValue({
			roomNumber: room.roomNumber,
			floor: room.floor,
			roomsCount: room.roomsCount.toString(),
			area: room.area,
			terraceArea: room.terraceArea || 0,
			finishType: room.finishType || "tamirsiz",
			basePrice:
				room.pricePerArea ??
				(room.area > 0 ? Math.round((room.basePrice / room.area) * 100) / 100 : 0),
			finishedPrice: room.finishedPrice || room.basePrice,
			hasCadastre: room.hasCadastre,
			isHighlighted: room.isHighlighted,
		});
		setEditModalVisible(true);
		navigate(location.pathname, {
			replace: true,
			state: { ...state, openEdit: undefined },
		});
	}, [location.pathname, location.state, navigate, room, canChangeRoomPrice, form]);

	if (isLoading) {
		return <div className="p-8 text-center text-gray-500">Yuklanmoqda...</div>;
	}

	if (!room) {
		return (
			<div className="crm-card pad empty">
				Xona topilmadi.
				<Button className="crm-mt" onClick={() => navigate(-1)}>
					Orqaga
				</Button>
			</div>
		);
	}

	// Backend doesn't return block/building IDs inside room right now, so we resolve them up the hierarchy
	const section = sections.find((s) => s.id === room.sectionId);
	const block = blocks.find((b) => b.id === section?.blockId);
	const building = buildings.find((b) => b.id === block?.buildingId);

	// Update room with resolved parent IDs if needed for links
	const roomBuildingId = building?.id || room.buildingId;
	const roomBlockId = block?.id || room.blockId;
	const maxFloor = block?.floorsCount;

	const inlineScheduleRows = linkedSaleId
		? (linkedSale?.schedule || []).slice(0, 6).map((item: any) => ({
				month: item.index,
				dueDate: item.date,
				amount: item.plannedAmount,
			}))
		: (calculatedSchedule?.schedulePreview || []).slice(0, 6);

	const handleToggleInlineSchedule = () => {
		const nextVisible = !showInlineSchedule;
		setShowInlineSchedule(nextVisible);

		if (!(nextVisible && !linkedSaleId && room.installmentPrice)) {
			return;
		}

		calculateScheduleMutation.mutate({
			installmentPrice: room.installmentPrice,
			monthsCount: room.installmentMonths || 12,
			initialPaymentPercent: room.minInitialPaymentPercent ?? 30,
		});
	};

	const canManageRoomStructure = canManageObjectStructure(role);

	const handleDeleteRoom = () => {
		Modal.confirm({
			title: "Xonani o'chirish",
			content: `Haqiqatan ham #${room.roomNumber}-xonani o'chirib tashlamoqchimisiz? Bu amalni ortga qaytarib bo'lmaydi.`,
			okText: "O'chirish",
			okType: "danger",
			cancelText: "Bekor qilish",
			onOk: () => {
				return deleteRoomMutation.mutateAsync();
			},
		});
	};

	const handleDeleteImage = (type: "image" | "image3d" | "terassaImage") => {
		if (!canChangeRoomPrice) {
			message.warning("Menejer xona ma'lumotlarini o'zgartira olmaydi.");
			return;
		}

		Modal.confirm({
			title: "Rasmni o'chirish",
			content: "Haqiqatan ham ushbu rasmni o'chirib tashlamoqchimisiz?",
			okText: "O'chirish",
			okType: "danger",
			cancelText: "Bekor qilish",
			onOk: () => {
				updateRoomMutation.mutate({
					id: room.id,
					payload: {
						[type]: "",
					},
				});
			},
		});
	};

	const handleOpenEditModal = () => {
		if (!canChangeRoomPrice) {
			message.warning("Menejer xona ma'lumotlarini o'zgartira olmaydi.");
			return;
		}

		form.setFieldsValue({
			roomNumber: room.roomNumber,
			floor: room.floor,
			roomsCount: room.roomsCount.toString(),
			area: room.area,
			terraceArea: room.terraceArea || 0,
			finishType: room.finishType || "tamirsiz",
			basePrice:
				room.pricePerArea ??
				(room.area > 0 ? Math.round((room.basePrice / room.area) * 100) / 100 : 0),
			finishedPrice: room.finishedPrice || room.basePrice,
			hasCadastre: room.hasCadastre,
			isHighlighted: room.isHighlighted,
		});
		setEditModalVisible(true);
	};

	const closeEditModal = () => {
		setEditModalVisible(false);
		setPlanFile(undefined);
		setImage3dFile(undefined);
		setTerraceFile(undefined);
	};

	const handleSaveEdit = async () => {
		if (!canChangeRoomPrice) {
			message.warning("Menejer xona ma'lumotlarini o'zgartira olmaydi.");
			return;
		}

		form.validateFields().then(async (values) => {
			try {
				const pricePerArea = toBackendPricePerArea(values.basePrice);
				if (!pricePerArea) {
					message.error("1 m² narxini kiriting");
					form.scrollToField("basePrice");
					return;
				}
				const [plan, image3d, terrace] = await Promise.all([
					planFile ? filesApi.uploadFile(planFile, "image") : Promise.resolve(undefined),
					image3dFile ? filesApi.uploadFile(image3dFile, "image") : Promise.resolve(undefined),
					terraceFile ? filesApi.uploadFile(terraceFile, "image") : Promise.resolve(undefined),
				]);

				updateRoomMutation.mutate({
					id: room.id,
					payload: {
						roomNumber: values.roomNumber,
						floor: values.floor,
						roomsCount: parseInt(values.roomsCount, 10),
						area: values.area.toString(),
						pricePerArea,
						priceRemont: calculatePriceRemont({
							area: values.area,
							finishedPrice: values.finishedPrice,
							pricePerArea: values.basePrice,
						}),
						roomChoice: values.finishType,
						sectionId: room.sectionId,
						...(plan?.url ? { image: plan.url } : {}),
						...(image3d?.url ? { image3d: image3d.url } : {}),
						...(terrace?.url ? { terassaImage: terrace.url } : {}),
					},
				});
			} catch (error) {
				message.error(parseApiError(error) || "Xona rasmlarini yuklashda xatolik yuz berdi");
			}
		});
	};

	const getStatusBadgeClass = (status: string) => {
		switch (status) {
			case "sotuvda":
				return "green";
			case "band":
				return "amber";
			case "sotilgan":
				return "slate";
			case "bekor":
				return "red";
			default:
				return "slate";
		}
	};

	const getStatusLabel = (status: string) => {
		switch (status) {
			case "sotuvda":
				return "Sotuvda";
			case "band":
				return "Band qilingan";
			case "sotilgan":
				return "Sotilgan";
			case "bekor":
				return "Bekor qilingan";
			default:
				return status;
		}
	};

	return (
		<div className="crm-room-detail">
			<div className="crm-page-head">
				<div>
					<Button
						className="crm-back-link"
						icon={<LeftOutlined />}
						onClick={() => {
							const orgIdQuery = building?.orgId ? `&orgId=${building.orgId}` : "";
							navigate(`/rooms?buildingId=${roomBuildingId}&blockId=${roomBlockId}${orgIdQuery}`, {
								state: location.state,
							});
						}}
					>
						Xonalar xaritasiga
					</Button>
					<h1>Xona #{room.roomNumber}</h1>
					<p style={{ color: "var(--crm-ink-3)" }}>
						{building?.name} · {block?.name} · {section?.name}
					</p>
				</div>
				<div className="crm-room-detail-actions crm-detail-actions">
					<Button
						onClick={() => {
							const orgIdQuery = building?.orgId ? `&orgId=${building.orgId}` : "";
							navigate(`/rooms?buildingId=${roomBuildingId}&blockId=${roomBlockId}${orgIdQuery}`, {
								state: location.state,
							});
						}}
					>
						Xaritaga
					</Button>
					{canManageRoomStructure && (room.status === "sotuvda" || room.status === "bekor") && (
						<Button
							danger
							icon={<DeleteOutlined />}
							loading={deleteRoomMutation.isPending}
							onClick={handleDeleteRoom}
						>
							O'chirish
						</Button>
					)}
					{canChangeRoomPrice && (room.status === "sotuvda" || room.status === "bekor") && (
						<Button icon={<DollarOutlined />} onClick={handleOpenPricingModal}>
							Narx belgilash
						</Button>
					)}
					{room.status === "sotuvda" && canCreateRoomSale && (
						<Button
							className="crm-primary-btn"
							icon={<ShoppingCartOutlined />}
							onClick={() =>
								navigate("/sales/new", {
									state: {
										buildingId: roomBuildingId,
										blockId: roomBlockId,
										roomId: room.id,
									},
								})
							}
						>
							Sotish
						</Button>
					)}
				</div>
			</div>

			<div className="crm-grid crm-form-grid">
				{/* Left: Obyekt ma'lumoti */}
				<div className="crm-card">
					<div className="crm-card-head">
						<h3>Obyekt ma'lumoti</h3>
						<div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
							<span className={`crm-status-badge ${getStatusBadgeClass(room.status)}`}>
								<span /> {getStatusLabel(room.status)}
							</span>
							{canChangeRoomPrice ? (
								<Button icon={<EditOutlined />} onClick={handleOpenEditModal}>
									Tahrirlash
								</Button>
							) : null}
						</div>
					</div>
					<div className="crm-card-body crm-room-detail-body">
						<div
							className="row gap-s wrap"
							style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "16px" }}
						>
							<RoomImagePreview
								label="xona plani"
								src={room.image}
								onDelete={canChangeRoomPrice ? () => handleDeleteImage("image") : undefined}
							/>
							<RoomImagePreview
								label="3D ko'rinish"
								src={room.image3d}
								onDelete={canChangeRoomPrice ? () => handleDeleteImage("image3d") : undefined}
							/>
							<RoomImagePreview
								label="terrasa plani"
								src={room.terassaImage}
								onDelete={canChangeRoomPrice ? () => handleDeleteImage("terassaImage") : undefined}
							/>
						</div>

						<div className="crm-divider" />

						<div className="crm-grid g2 crm-room-kv-grid">
							<div className="crm-kv">
								<span>Xona raqami</span>
								<strong>{room.roomNumber}</strong>
							</div>
							<div className="crm-kv">
								<span>Qavat</span>
								<strong>{room.floor}</strong>
							</div>
							<div className="crm-kv">
								<span>Xonalar soni</span>
								<strong>{room.roomsCount}-xonali</strong>
							</div>
							<div className="crm-kv">
								<span>Maydon</span>
								<strong>{room.area} m²</strong>
							</div>
							<div className="crm-kv">
								<span>Terrasa</span>
								<strong>{room.terraceArea > 0 ? `${room.terraceArea} m²` : "Yo'q"}</strong>
							</div>
							<div className="crm-kv">
								<span>Ta'mir turi</span>
								<strong style={{ textTransform: "capitalize" }}>{room.finishType}</strong>
							</div>
							<div className="crm-kv">
								<span>Asosiy narx</span>
								<strong className="num">{formatMoney(room.basePrice)} UZS</strong>
							</div>
							<div className="crm-kv">
								<span>Kadastr</span>
								<strong>{room.hasCadastre ? "Bor" : "Yo'q"}</strong>
							</div>
							{room.installmentPrice && (
								<>
									<div className="crm-kv">
										<span>Bo'lib to'lash narxi</span>
										<strong className="num">{formatMoney(room.installmentPrice)} UZS</strong>
									</div>
									<div className="crm-kv">
										<span>Bo'lib to'lash muddati</span>
										<strong>{room.installmentMonths} oy</strong>
									</div>
									<div className="crm-kv">
										<span>Boshlang'ich minimal to'lov</span>
										<strong>{room.minInitialPaymentPercent}%</strong>
									</div>
								</>
							)}
						</div>
					</div>
				</div>

				{/* Right: Sotuv / mijoz */}
				<div className="crm-card">
					<div className="crm-card-head">
						<h3>Sotuv / mijoz</h3>
						{room.client ? (
							<span className="crm-status-badge green">{room.client.status}</span>
						) : (
							<span className="crm-status-badge red">Mijoz biriktirilmagan</span>
						)}
					</div>
					<div className="crm-card-body">
						{room.client ? (
							<>
								<div
									className="row center gap-s"
									style={{ display: "flex", gap: "10px", alignItems: "center" }}
								>
									<Avatar
										className="crm-avatar"
										style={{
											background: "var(--crm-primary-soft)",
											color: "var(--crm-primary-700)",
										}}
									>
										{room.client.avatar}
									</Avatar>
									<div>
										<strong style={{ fontSize: "15px", display: "block" }}>
											{room.client.name}
										</strong>
										<span style={{ fontSize: "12px", color: "var(--crm-ink-3)" }}>
											{room.client.phone} · Menejer: {room.client.manager}
										</span>
									</div>
								</div>

								<div className="crm-divider" />

								<div
									className="row between center"
									style={{
										display: "flex",
										justifyContent: "space-between",
										fontSize: "12px",
										color: "var(--crm-ink-2)",
									}}
								>
									<span>To'langan</span>
									<strong>{room.client.percentPaid}%</strong>
								</div>

								<div className="crm-prog crm-mt-s" style={{ marginTop: "8px" }}>
									<div
										className="crm-prog-fill green"
										style={{ width: `${room.client.percentPaid}%` }}
									/>
								</div>

								<div className="crm-mt" style={{ marginTop: "16px" }}>
									<div className="crm-kv">
										<span>Keyingi to'lov</span>
										<strong>{room.client.nextPaymentDate}</strong>
									</div>
									<div className="crm-kv">
										<span>Summa</span>
										<strong>{room.client.nextPaymentAmount}</strong>
									</div>
									<div className="crm-kv">
										<span>Qoldiq</span>
										<strong>{room.client.debtBalance}</strong>
									</div>
								</div>

								<div
									className="crm-form-actions crm-mt"
									style={{ display: "flex", gap: "8px", marginTop: "18px" }}
								>
									<Button
										style={{ flex: 1 }}
										onClick={() =>
											navigate(`/clients/${room.client?.id}`, { state: { fromDeepNav: true } })
										}
									>
										Mijoz kartochkasi
									</Button>
									{linkedSaleId || room.installmentPrice ? (
										<Button
											icon={<CalendarOutlined />}
											type={showInlineSchedule ? "primary" : "default"}
											onClick={handleToggleInlineSchedule}
										/>
									) : null}
								</div>
								{showInlineSchedule ? (
									<div
										className="crm-mt"
										style={{
											marginTop: "14px",
											border: "1px solid var(--crm-border-color)",
											borderRadius: "8px",
											overflow: "hidden",
											background: "#fff",
										}}
									>
										<div
											style={{
												display: "grid",
												gridTemplateColumns: "64px 1fr 120px",
												gap: "8px",
												padding: "10px 12px",
												background: "var(--crm-surface-2)",
												color: "var(--crm-ink-2)",
												fontSize: "12px",
												fontWeight: 700,
											}}
										>
											<span>Oy</span>
											<span>Sana</span>
											<span style={{ textAlign: "right" }}>Summa</span>
										</div>
										{inlineScheduleRows.length > 0 ? (
											inlineScheduleRows.map((item: any) => (
												<div
													key={`${item.month}-${item.dueDate}`}
													style={{
														display: "grid",
														gridTemplateColumns: "64px 1fr 120px",
														gap: "8px",
														padding: "10px 12px",
														borderTop: "1px solid var(--crm-border-color)",
														fontSize: "12px",
													}}
												>
													<span>{item.month}-oy</span>
													<span>{item.month === 1 ? "Boshlang'ich to'lov" : item.dueDate}</span>
													<strong className="num" style={{ textAlign: "right" }}>
														{formatUzsAmount(item.amount)}
													</strong>
												</div>
											))
										) : (
											<div
												style={{
													padding: "12px",
													borderTop: "1px solid var(--crm-border-color)",
													color: "var(--crm-ink-3)",
													fontSize: "12px",
												}}
											>
												Grafik yuklanmoqda...
											</div>
										)}
										{linkedSaleId && linkedSale?.schedule?.length ? (
											<div
												style={{
													padding: "10px 12px",
													borderTop: "1px solid var(--crm-border-color)",
												}}
											>
												<Button
													size="small"
													block
													onClick={() =>
														navigate(`/sales/${linkedSaleId}/schedule`, {
															state: {
																...location.state,
																returnToRoomId: room.id,
																returnToRoomLabel: `Xona #${room.roomNumber}`,
															},
														})
													}
												>
													To'liq grafik
												</Button>
											</div>
										) : null}
									</div>
								) : null}
							</>
						) : showPredictiveSchedule ? (
							<>
								<div
									style={{
										fontSize: "12px",
										color: "var(--crm-ink-3)",
										marginBottom: "4px",
									}}
								>
									Xona hali sotilmagan. Quyida bo'lib to'lash narxi asosidagi bashoratli to'lov
									grafigi ko'rsatilgan.
								</div>

								<div className="crm-kv">
									<span>Oylik to'lov</span>
									<strong className="num">
										{predictiveSchedule ? formatUzsAmount(predictiveSchedule.monthlyPayment) : "—"}
									</strong>
								</div>
								<div className="crm-kv">
									<span>Boshlang'ich to'lov</span>
									<strong className="num">
										{predictiveSchedule
											? formatUzsAmount(predictiveSchedule.initialPaymentAmount)
											: "—"}
									</strong>
								</div>
								<div className="crm-kv">
									<span>Muddat</span>
									<strong>{room.installmentMonths || 12} oy</strong>
								</div>

								<div className="crm-divider" />

								{isPredictiveScheduleLoading ? (
									<div
										style={{
											textAlign: "center",
											padding: "40px 0",
											color: "var(--crm-ink-3)",
											fontSize: "12px",
										}}
									>
										Bashorat hisoblanmoqda...
									</div>
								) : predictiveSchedule ? (
									<div
										style={{
											border: "1px solid var(--crm-border-color)",
											borderRadius: "8px",
											overflow: "hidden",
											background: "#fff",
										}}
									>
										<div
											style={{
												display: "grid",
												gridTemplateColumns: "64px 1fr 120px",
												gap: "8px",
												padding: "10px 12px",
												background: "var(--crm-surface-2)",
												color: "var(--crm-ink-2)",
												fontSize: "12px",
												fontWeight: 700,
											}}
										>
											<span>Oy</span>
											<span>Sana</span>
											<span style={{ textAlign: "right" }}>Summa</span>
										</div>
										<div style={{ maxHeight: "260px", overflowY: "auto" }}>
											{predictiveSchedule.schedulePreview.map((item) => (
												<div
													key={item.month}
													style={{
														display: "grid",
														gridTemplateColumns: "64px 1fr 120px",
														gap: "8px",
														padding: "10px 12px",
														borderTop: "1px solid var(--crm-border-color)",
														fontSize: "12px",
													}}
												>
													<span>{item.month}-oy</span>
													<span>{item.month === 1 ? "Boshlang'ich to'lov" : item.dueDate}</span>
													<strong className="num" style={{ textAlign: "right" }}>
														{formatUzsAmount(item.amount)}
													</strong>
												</div>
											))}
										</div>
									</div>
								) : null}
							</>
						) : (
							<div
								style={{
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									height: "220px",
									textAlign: "center",
									color: "var(--crm-ink-3)",
								}}
							>
								<InfoCircleOutlined
									style={{ fontSize: "32px", marginBottom: "12px", opacity: 0.5 }}
								/>
								<strong>Xona sotuvda bo'sh turibdi</strong>
								<p style={{ fontSize: "12px", maxWidth: "260px", marginTop: "6px" }}>
									Ushbu xona bo'yicha hech qanday shartnoma yoki sotuv mavjud emas. Sotishni
									boshlash uchun yuqoridagi tugmani bosing.
								</p>
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Edit Price Modal */}
			{/* Edit Room Modal */}
			<Modal
				title="Xonani tahrirlash"
				open={editModalVisible}
				onOk={handleSaveEdit}
				onCancel={closeEditModal}
				okText="Saqlash"
				cancelText="Bekor"
				okButtonProps={{ className: "crm-primary-btn", loading: updateRoomMutation.isPending }}
				width={650}
				centered
				className="crm-scrollable-modal"
			>
				<Form form={form} layout="vertical" style={{ marginTop: "16px" }}>
					<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
						<Form.Item
							name="roomNumber"
							label="Xona raqami"
							rules={[{ required: true, message: "Xona raqamini kiriting!" }]}
							style={{ marginBottom: "12px" }}
						>
							<Input size="middle" placeholder="503" />
						</Form.Item>

						<Form.Item
							name="floor"
							label="Qavat"
							rules={[
								{ required: true, message: "Qavatni kiriting!" },
								{
									validator: (_, value) => {
										if (!(value && maxFloor) || value <= maxFloor) {
											return Promise.resolve();
										}
										return Promise.reject(
											new Error(`Tanlangan blokda maksimal ${maxFloor} qavat bor`)
										);
									},
								},
							]}
							style={{ marginBottom: "12px" }}
						>
							<InputNumber
								max={maxFloor}
								min={1}
								size="middle"
								style={{ width: "100%" }}
								placeholder="5"
							/>
						</Form.Item>

						<Form.Item
							name="roomsCount"
							label="Xonalar soni"
							rules={[{ required: true, message: "Xonalar sonini tanlang!" }]}
							style={{ marginBottom: "12px" }}
						>
							<Select size="middle">
								<Select.Option value="1">1-xonali</Select.Option>
								<Select.Option value="2">2-xonali</Select.Option>
								<Select.Option value="3">3-xonali</Select.Option>
								<Select.Option value="4">4-xonali</Select.Option>
							</Select>
						</Form.Item>
					</div>

					<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
						<Form.Item
							name="area"
							label="Maydon (m²)"
							rules={[{ required: true, message: "Maydonni kiriting!" }]}
							style={{ marginBottom: "12px" }}
						>
							<InputNumber min={10} size="middle" style={{ width: "100%" }} placeholder="72.5" />
						</Form.Item>

						<Form.Item name="terraceArea" label="Terrasa (m²)" style={{ marginBottom: "12px" }}>
							<InputNumber min={0} size="middle" style={{ width: "100%" }} placeholder="0" />
						</Form.Item>

						<Form.Item
							name="finishType"
							label="Ta'mir turi"
							rules={[{ required: true, message: "Ta'mir turini tanlang!" }]}
							style={{ marginBottom: "12px" }}
						>
							<Select size="middle">
								<Select.Option value="tamirsiz">Ta'mirsiz</Select.Option>
								<Select.Option value="tamirli">Ta'mirli</Select.Option>
							</Select>
						</Form.Item>
					</div>

					<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
						<Form.Item
							name="basePrice"
							label="1 m² narxi (UZS)"
							rules={[{ required: true, message: "1 m² narxini kiriting!" }]}
							style={{ marginBottom: "12px" }}
						>
							<InputNumber
								min={1}
								size="middle"
								style={{ width: "100%" }}
								placeholder="7 500 000"
								{...moneyInputProps}
							/>
						</Form.Item>

						<Form.Item
							name="finishedPrice"
							label="Ta'mirli umumiy narx (UZS)"
							style={{ marginBottom: "12px" }}
						>
							<InputNumber
								min={0}
								size="middle"
								style={{ width: "100%" }}
								placeholder="0"
								{...moneyInputProps}
							/>
						</Form.Item>
					</div>

					<div className="crm-room-upload-grid">
						<RoomUploadField
							existingAsset={room.image}
							file={planFile}
							label="Xona plani"
							onFileChange={setPlanFile}
						/>
						<RoomUploadField
							existingAsset={room.image3d}
							file={image3dFile}
							label="3D ko'rinish"
							onFileChange={setImage3dFile}
						/>
						<RoomUploadField
							existingAsset={room.terassaImage}
							file={terraceFile}
							label="Terrasa plani"
							onFileChange={setTerraceFile}
						/>
					</div>

					<div style={{ display: "flex", gap: "20px", marginTop: "8px", marginBottom: "8px" }}>
						<Form.Item name="hasCadastre" valuePropName="checked" noStyle>
							<Checkbox>Kadastrlashtirilgan</Checkbox>
						</Form.Item>
						<Form.Item name="isHighlighted" valuePropName="checked" noStyle>
							<Checkbox>Xaritada ajratish</Checkbox>
						</Form.Item>
					</div>
				</Form>
			</Modal>

			{/* Pricing Modal */}
			<Modal
				title="Bo'lib to'lash narxi va shartlarini sozlash"
				open={pricingModalVisible}
				onOk={handleSavePricing}
				onCancel={() => setPricingModalVisible(false)}
				okText="Saqlash"
				cancelText="Bekor"
				okButtonProps={{
					className: "crm-primary-btn",
					loading: savePricingMutation.isPending,
				}}
				width={650}
				centered
				className="crm-scrollable-modal"
			>
				<Form
					form={pricingForm}
					layout="vertical"
					style={{ marginTop: "16px" }}
					onValuesChange={handlePricingFormValuesChange}
				>
					<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
						<Form.Item label="Naqdga sotuv narxi (UZS)">
							<InputNumber
								value={room.finishedPrice || room.basePrice}
								disabled
								className="w-full"
								style={{ width: "100%" }}
								{...moneyInputProps}
							/>
						</Form.Item>

						<Form.Item
							name="installmentPrice"
							label="Bo'lib to'lash narxi (UZS)"
							rules={[
								{ required: true, message: "Bo'lib to'lash narxini kiriting!" },
								{
									validator: (_, value) => {
										const base = room.finishedPrice || room.basePrice;
										if (!value || value >= base) {
											return Promise.resolve();
										}
										return Promise.reject(
											new Error(
												"Bo'lib to'lash narxi naqdga sotuv narxidan past bo'lishi mumkin emas"
											)
										);
									},
								},
							]}
						>
							<InputNumber
								min={1}
								className="w-full"
								style={{ width: "100%" }}
								{...moneyInputProps}
							/>
						</Form.Item>
					</div>

					<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
						<Form.Item
							name="installmentMonths"
							label="Bo'lib to'lash oylari soni"
							rules={[{ required: true, message: "Oylar sonini kiriting!" }]}
						>
							<InputNumber min={1} max={120} className="w-full" style={{ width: "100%" }} />
						</Form.Item>

						<Form.Item
							name="minInitialPaymentPercent"
							label="Boshlang'ich minimal to'lov (%)"
							rules={[{ required: true, message: "Minimal foizni kiriting!" }]}
						>
							<InputNumber min={0} max={100} className="w-full" style={{ width: "100%" }} />
						</Form.Item>
					</div>

					<div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "16px" }}>
						<Button
							type="dashed"
							onClick={handleCalculateSchedule}
							loading={calculateScheduleMutation.isPending}
						>
							Grafikni oldindan ko'rish
						</Button>
					</div>

					{calculatedSchedule && (
						<div
							style={{
								marginTop: "16px",
								padding: "16px",
								background: "#f8fafd",
								border: "1px solid #e1e8f5",
								borderRadius: "8px",
							}}
						>
							<h4 style={{ marginBottom: "12px", fontWeight: "bold" }}>
								Hisoblangan grafik (Predprosmotr)
							</h4>

							<div
								style={{
									display: "grid",
									gridTemplateColumns: "1fr 1fr 1fr",
									gap: "12px",
									marginBottom: "16px",
								}}
							>
								<div>
									<div className="text-xs text-gray-500" style={{ marginBottom: "4px" }}>
										Bo'lib to'lash narxi
									</div>
									<InputNumber
										value={calculatedSchedule.installmentPrice}
										disabled
										className="w-full"
										style={{ width: "100%" }}
									/>
								</div>
								<div>
									<div className="text-xs text-gray-500" style={{ marginBottom: "4px" }}>
										Oylar soni
									</div>
									<InputNumber
										min={1}
										max={120}
										value={scheduleMonthsCount}
										onChange={(val) => {
											const newVal = val || 1;
											setScheduleMonthsCount(newVal);
											pricingForm.setFieldsValue({ installmentMonths: newVal });
											calculateScheduleMutation.mutate({
												installmentPrice: pricingForm.getFieldValue("installmentPrice"),
												monthsCount: newVal,
												initialPaymentPercent: scheduleDownPaymentPercent,
											});
										}}
										className="w-full"
										style={{ width: "100%" }}
									/>
								</div>
								<div>
									<div className="text-xs text-gray-500" style={{ marginBottom: "4px" }}>
										Boshlang'ich to'lov (%)
									</div>
									<InputNumber
										min={pricingForm.getFieldValue("minInitialPaymentPercent") || 0}
										max={100}
										value={scheduleDownPaymentPercent}
										onChange={(val) => {
											const currentMin = pricingForm.getFieldValue("minInitialPaymentPercent") || 0;
											const newVal = val || currentMin;
											setScheduleDownPaymentPercent(newVal);
											calculateScheduleMutation.mutate({
												installmentPrice: pricingForm.getFieldValue("installmentPrice"),
												monthsCount: scheduleMonthsCount,
												initialPaymentPercent: newVal,
											});
										}}
										className="w-full"
										style={{ width: "100%" }}
									/>
								</div>
							</div>

							<div
								style={{
									display: "grid",
									gridTemplateColumns: "1fr 1fr",
									gap: "12px",
									marginBottom: "16px",
									padding: "8px 12px",
									background: "#fff",
									borderRadius: "6px",
									border: "1px solid #eef2f8",
								}}
							>
								<div>
									<div className="text-xs text-gray-400">Boshlang'ich summa:</div>
									<div className="font-semibold text-sm" style={{ color: "var(--crm-ink-1)" }}>
										{formatMoney(calculatedSchedule.initialPaymentAmount)} UZS
									</div>
								</div>
								<div>
									<div className="text-xs text-gray-400">Oylik to'lov summasi:</div>
									<div className="font-semibold text-sm" style={{ color: "var(--crm-ink-1)" }}>
										{formatMoney(calculatedSchedule.monthlyPayment)} UZS
									</div>
								</div>
							</div>

							<div
								className="crm-slim-scrollbar"
								style={{
									maxHeight: "200px",
									overflowY: "auto",
									border: "1px solid #e8e8e8",
									borderRadius: "6px",
									background: "#fff",
								}}
							>
								<table style={{ width: "100%", borderCollapse: "collapse" }}>
									<thead>
										<tr style={{ background: "#fafafa", borderBottom: "1px solid #e8e8e8" }}>
											<th
												style={{
													padding: "8px",
													textAlign: "left",
													fontSize: "12px",
													color: "var(--crm-ink-2)",
												}}
											>
												Oy
											</th>
											<th
												style={{
													padding: "8px",
													textAlign: "left",
													fontSize: "12px",
													color: "var(--crm-ink-2)",
												}}
											>
												Sana
											</th>
											<th
												style={{
													padding: "8px",
													textAlign: "right",
													fontSize: "12px",
													color: "var(--crm-ink-2)",
												}}
											>
												Summa
											</th>
										</tr>
									</thead>
									<tbody>
										{calculatedSchedule.schedulePreview.map((item: any) => (
											<tr key={item.month} style={{ borderBottom: "1px solid #f0f0f0" }}>
												<td
													style={{
														padding: "8px",
														fontSize: "12px",
														color: "var(--crm-ink-1)",
													}}
												>
													{item.month}-oy
												</td>
												<td
													style={{
														padding: "8px",
														fontSize: "12px",
														color: "var(--crm-ink-1)",
													}}
												>
													{item.dueDate}
												</td>
												<td
													style={{
														padding: "8px",
														fontSize: "12px",
														textAlign: "right",
														fontWeight: "600",
														color: "var(--crm-ink-1)",
													}}
												>
													{formatMoney(item.amount)} UZS
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					)}
				</Form>
			</Modal>
		</div>
	);
}
